import React from 'react';

interface PointerBoundaryProps extends React.HTMLAttributes<HTMLSpanElement> {
  onClickAway?: () => void;
}

// DOM node that receives a forwarded ref and pointer handlers, and reports
// clicks outside itself. The child does not have to be the event target.
const PointerBoundary = React.forwardRef<HTMLSpanElement, PointerBoundaryProps>(function PointerBoundary(
  { onClickAway, children, ...props },
  ref,
) {
  const nodeRef = React.useRef<HTMLSpanElement | null>(null);
  const onClickAwayRef = React.useRef(onClickAway);
  onClickAwayRef.current = onClickAway;
  const listenForClickAway = onClickAway != null;

  const setRef = (node: HTMLSpanElement | null) => {
    nodeRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLSpanElement | null>).current = node;
    }
  };

  React.useEffect(() => {
    if (!listenForClickAway) {
      return undefined;
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && nodeRef.current?.contains(target)) {
        return;
      }
      onClickAwayRef.current?.();
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [listenForClickAway]);

  return (
    <span ref={setRef} {...props}>
      {children}
    </span>
  );
});

export default PointerBoundary;
